import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { Product } from '@/services/products';
import { useModelThumbnail } from '@/hooks/useModelThumbnail';

interface ProductCardProps {
  readonly product: Product;
}

export const ProductCard = ({ product }: ProductCardProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const { url: thumbnailUrl, isGenerating } = useModelThumbnail(
    product.productId,
    product.fileKey,
  );

  // Prefer 3D model thumbnail, fall back to uploaded image
  const displayImage = thumbnailUrl ?? (product.imageKey ? `/product-images/${product.imageKey}` : null);

  return (
    <Link
      to={`/catalog/${product.productId}`}
      style={{ textDecoration: 'none', display: 'block' }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div
        style={{
          borderRadius: 'var(--radius-xl)',
          border: '1px solid var(--border-default)',
          backgroundColor: 'var(--bg-surface)',
          overflow: 'hidden',
          transition: 'box-shadow 200ms ease, transform 200ms ease',
          boxShadow: isHovered ? 'var(--shadow-md)' : 'none',
          transform: isHovered ? 'translateY(-2px)' : 'translateY(0)',
          cursor: 'pointer',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Image area */}
        <div
          style={{
            position: 'relative',
            aspectRatio: '4 / 3',
            backgroundColor: 'var(--bg-surface-sunken)',
            overflow: 'hidden',
          }}
        >
          {displayImage ? (
            <img
              src={displayImage}
              alt={product.name}
              loading="lazy"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                transition: 'transform 300ms ease',
                transform: isHovered ? 'scale(1.04)' : 'scale(1)',
              }}
            />
          ) : isGenerating ? (
            <div
              className="flex flex-col items-center justify-center"
              style={{
                width: '100%',
                height: '100%',
                gap: 'var(--space-2)',
              }}
            >
              <div
                className="animate-spin"
                style={{
                  width: '2rem',
                  height: '2rem',
                  borderRadius: '50%',
                  border: '2px solid var(--color-neutral-200)',
                  borderTopColor: 'var(--color-primary-400)',
                }}
              />
              <span
                style={{
                  fontSize: 'var(--text-xs)',
                  color: 'var(--color-neutral-400)',
                  fontWeight: 'var(--font-weight-medium)',
                }}
              >
                Rendering preview...
              </span>
            </div>
          ) : (
            <div
              className="flex flex-col items-center justify-center"
              style={{
                width: '100%',
                height: '100%',
                gap: 'var(--space-2)',
              }}
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.2}
                style={{
                  width: '3rem',
                  height: '3rem',
                  color: 'var(--color-neutral-400)',
                  transition: 'color 200ms ease',
                  ...(isHovered ? { color: 'var(--color-primary-400)' } : {}),
                }}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z" />
                <polyline strokeLinecap="round" strokeLinejoin="round" points="3.27 6.96 12 12.01 20.73 6.96" />
                <line x1="12" y1="22.08" x2="12" y2="12" />
              </svg>
              <span
                style={{
                  fontSize: 'var(--text-xs)',
                  color: 'var(--color-neutral-400)',
                  fontWeight: 'var(--font-weight-medium)',
                  letterSpacing: '0.05em',
                  textTransform: 'uppercase',
                }}
              >
                3D Model
              </span>
            </div>
          )}

          {/* Category badge overlay */}
          <span
            style={{
              position: 'absolute',
              top: 'var(--space-3)',
              left: 'var(--space-3)',
              borderRadius: 'var(--radius-full)',
              backgroundColor: 'rgba(0, 0, 0, 0.55)',
              backdropFilter: 'blur(6px)',
              WebkitBackdropFilter: 'blur(6px)',
              padding: '3px 10px',
              fontSize: '11px',
              fontWeight: 500,
              color: '#fff',
              letterSpacing: '0.02em',
            }}
          >
            {product.category}
          </span>
        </div>

        {/* Content area */}
        <div
          style={{
            padding: 'var(--space-4)',
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
          }}
        >
          <h3
            style={{
              fontSize: 'var(--text-base)',
              fontWeight: 'var(--font-weight-semibold)',
              color: 'var(--text-primary)',
              lineHeight: 'var(--leading-snug)',
            }}
          >
            {product.name}
          </h3>
          <p
            className="line-clamp-2"
            style={{
              marginTop: 'var(--space-1)',
              fontSize: 'var(--text-sm)',
              color: 'var(--text-tertiary)',
              lineHeight: 'var(--leading-relaxed)',
            }}
          >
            {product.description}
          </p>

          {/* Materials */}
          {product.materials.length > 0 && (
            <div
              className="flex flex-wrap"
              style={{
                marginTop: 'var(--space-3)',
                gap: '4px',
              }}
            >
              {product.materials.slice(0, 3).map((mat) => (
                <span
                  key={mat}
                  style={{
                    borderRadius: 'var(--radius-sm)',
                    backgroundColor: 'var(--bg-surface-sunken)',
                    padding: '2px 6px',
                    fontSize: '10px',
                    fontWeight: 500,
                    color: 'var(--text-tertiary)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                  }}
                >
                  {mat}
                </span>
              ))}
              {product.materials.length > 3 && (
                <span
                  style={{
                    padding: '2px 4px',
                    fontSize: '10px',
                    color: 'var(--text-tertiary)',
                  }}
                >
                  +{product.materials.length - 3}
                </span>
              )}
            </div>
          )}

          {/* Price - pushed to bottom */}
          <div
            className="flex items-center justify-between"
            style={{
              marginTop: 'auto',
              paddingTop: 'var(--space-3)',
            }}
          >
            <span
              style={{
                fontSize: 'var(--text-lg)',
                fontWeight: 'var(--font-weight-bold)',
                color: 'var(--text-primary)',
              }}
            >
              ${product.basePrice.toFixed(2)}
            </span>
            <span
              style={{
                fontSize: 'var(--text-xs)',
                color: isHovered ? 'var(--text-accent)' : 'var(--text-tertiary)',
                fontWeight: 'var(--font-weight-medium)',
                transition: 'color 200ms ease',
              }}
            >
              View details →
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
};
